import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as styles from './ColorMenu.css';
import * as toolbarStyles from '../FloatingMenu.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Button, IconButton } from '@mutiny-pkg/dumpster-ui/components/Button';
import { Typography } from '@mutiny-pkg/dumpster-ui/components/Typography';
import { Flex } from '@mutiny-pkg/dumpster-ui/components/Flex';
import { Icons } from '@mutiny-pkg/dumpster-ui/icons';
import { CLOSE_LINK_EDITOR } from '@mutiny-pkg/bin-ui/src/richtext/RichInput/lexicalConfig';
import Sketch from '@uiw/react-color-sketch';
import { debounce } from 'lodash-es';
import { CANONICAL_COLOR_ORDER } from '@mutiny-pkg/bin-ui/src/rigidMicrosites/theme/constants';
import { ColorMenuType } from '@mutiny-pkg/bin-ui/src/richtext/FloatingMenuPlugin/ColorMenu/types';
import { LexicalThemeColors } from '../../types';

export const ColorMenu = React.forwardRef<
  HTMLDivElement,
  {
    type: ColorMenuType;
    onApplyColor: (color: string) => void;
    isButtonActive: boolean;
    currentColor: string;
    openColorMenuType: ColorMenuType | null;
    setOpenColorMenuType: (type: ColorMenuType | null) => void;
    themeColors?: LexicalThemeColors;
  }
>(({ type, onApplyColor, isButtonActive, currentColor, openColorMenuType, setOpenColorMenuType, themeColors }, ref) => {
  const show = openColorMenuType === type;
  const [editor] = useLexicalComposerContext();
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const [customColorPickerOpen, setCustomColorPickerOpen] = React.useState(false);
  const [selectedCustomColor, setSelectedCustomColor] = useState<string>(currentColor);

  const closeColorMenu = useCallback(() => {
    if (customColorPickerOpen) {
      onApplyColor(selectedCustomColor);
    }
    setOpenColorMenuType(null);
    setCustomColorPickerOpen(false);
  }, [customColorPickerOpen, onApplyColor, selectedCustomColor, setOpenColorMenuType]);

  const handleApplyColor = (color: string) => {
    onApplyColor(color);
    setOpenColorMenuType(null);
    setCustomColorPickerOpen(false);
  };

  const handleSetCustomColorPickerOpen = (open: boolean) => {
    setSelectedCustomColor(currentColor);
    setCustomColorPickerOpen(open);
  };

  const handleButtonClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (show) {
        closeColorMenu();
      } else {
        setCustomColorPickerOpen(false);
        editor.dispatchCommand(CLOSE_LINK_EDITOR, true);
        setOpenColorMenuType(type);
      }
    },
    [show, closeColorMenu, editor, type, setOpenColorMenuType],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (show && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeColorMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeColorMenu, show]);

  // We need to capture MouseDown events here since otherwise chrome will change the selection to whatever
  // element is clicked. This will change the selection Lexical knows about and cause us to try and change
  // the clicked div element to have font/text color.
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = node;
      }
    },
    [ref],
  );

  return (
    <div style={{ position: 'relative' }} onMouseDown={handleMouseDown} ref={mergedRef}>
      <IconButton
        tabIndex={-1}
        className={toolbarStyles.toolbarButton({ active: isButtonActive || show })}
        label={type === ColorMenuType.Text ? 'Text Color' : 'Highlight Color'}
        icon={type === ColorMenuType.Text ? Icons.Text : Icons.Highlighter}
        variant="text"
        onMouseDown={handleButtonClick}
      />
      {show && (
        <Flex.Container flexDirection="column" className={styles.colorMenuPopover}>
          <Flex.Container paddingX="8u" paddingY="4u" fullWidth flexDirection="column">
            {customColorPickerOpen ? (
              <CustomColorPicker value={selectedCustomColor} onChange={setSelectedCustomColor} />
            ) : (
              <ThemeColorPicker
                onApplyColor={handleApplyColor}
                setColorPickerOpen={handleSetCustomColorPickerOpen}
                themeColors={themeColors}
              />
            )}
          </Flex.Container>
        </Flex.Container>
      )}
    </div>
  );
});

ColorMenu.displayName = 'ColorMenu';

type ThemeColorPickerProps = {
  onApplyColor: (color: string) => void;
  setColorPickerOpen: (open: boolean) => void;
  themeColors?: LexicalThemeColors;
};

const ThemeColorPicker = ({ onApplyColor, setColorPickerOpen, themeColors }: ThemeColorPickerProps) => {
  const colors = themeColors?.color;

  const handleCustomColorClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setColorPickerOpen(true);
  };

  const handleColorClick = (event: React.MouseEvent, color: string) => {
    event.preventDefault();
    event.stopPropagation();
    onApplyColor(color);
  };

  return (
    <>
      <Flex.Container justifyContent="space-between" alignItems="center" paddingY="4u" fullWidth>
        <Typography fontWeight="semibold" font="body1" margin="none">
          Theme Colors
        </Typography>
      </Flex.Container>

      <Flex.Container gap="4u" flexWrap="wrap" paddingY="4u" fullWidth>
        <div
          key="clear"
          className={styles.themeColorButton}
          onClick={(event: React.MouseEvent) => {
            handleColorClick(event, '');
          }}
        >
          <Icons.DropletOff />
        </div>
        {CANONICAL_COLOR_ORDER.map((colorName) => {
          if (!colors) return null;
          const color = colors[colorName];
          if (!color) return null;
          return (
            <div
              key={colorName}
              role="button"
              aria-label={colorName}
              tabIndex={0}
              className={styles.themeColorButton}
              style={{ backgroundColor: color }}
              onClick={(event: React.MouseEvent) => {
                handleColorClick(event, color);
              }}
            />
          );
        })}
        {themeColors?.additionalColors?.map((value) => (
          <div
            key={value}
            role="button"
            aria-label={value}
            tabIndex={0}
            className={styles.themeColorButton}
            style={{ backgroundColor: value }}
            onClick={(event: React.MouseEvent) => {
              handleColorClick(event, value);
            }}
          />
        ))}
      </Flex.Container>
      <Flex.Container>
        <Button onClick={handleCustomColorClick} variant="text">
          <Icons.Plus fontSize="20px" className={styles.plusIcon} />
          <Typography fontWeight="semibold" font="body1" color="electricPurple" margin="none">
            Custom color
          </Typography>
        </Button>
      </Flex.Container>
    </>
  );
};

type CustomColorPickerProps = {
  value: string;
  onChange: (color: string) => void;
};

const CustomColorPicker = ({ value, onChange }: CustomColorPickerProps) => {
  const debouncedOnChange = useMemo(() => debounce((color: string) => onChange(color), 300), [onChange]);

  return (
    <Sketch
      color={value}
      onChange={(color) => {
        debouncedOnChange(color.hexa);
      }}
      disableAlpha
    />
  );
};
